import { Theme } from './theme.interface';
import { Persona } from './personas.interface';

/**
 * Normalize persona_id to array format for backward compatibility.
 * Converts legacy string format to single-element array.
 *
 * @param personaId - Persona ID as string or array
 * @returns Persona ID as array
 */
export function normalizePersonaId(personaId: string | string[]): string[] {
  if (Array.isArray(personaId)) {
    return personaId;
  }
  return [personaId];
}

export interface ReviewSession {
  review_id: string;
  persona_id: string[];
  status: 'IN-PROGRESS' | 'DONE' | 'FAILED';
  document: string;
  themes: Theme[];
  created_at: string;
  created_by: string;
}

export interface ReviewSessionPost {
  persona_id: string[];
  document: string;
}

/**
 * Backend response interfaces (what we receive from API)
 * These interfaces match the structure returned by the Lambda handler
 */

/**
 * Backend finding item structure.
 * The backend returns arrays where each index represents one finding.
 * All arrays should have the same length.
 */
export interface BackendFindingItem {
  /** Array of finding descriptions - maps to ReviewInsight.finding */
  Finding: string[];
  /** Array of deviation descriptions - maps to ReviewInsight.deviation */
  Deviation: string[];
  /** Array of original text excerpts - maps to ReviewInsight.original_text */
  Original_text: string[];
  /** Array of section identifiers - maps to ReviewInsight.section */
  Original_text_section: string[];
  /** Array of subsection identifiers - maps to ReviewInsight.subsection */
  Original_text_subsection: string[];
  /** Array of recommendations - maps to ReviewInsight.recommendation */
  Recommendation: string[];
  /** Array of persona names - maps to ReviewInsight.persona.name */
  Persona_Name: string[];
  /** Array of severity levels - maps to ReviewInsight.severity */
  Severity_Level: string[];
  /** Array of duplicate field - maps to ReviewInsight.duplicate */
  Duplicate?: string[];
  /** Array of resolved statuses - maps to ReviewInsight.resolved */
  Resolved?: boolean[];
}

/**
 * Backend review session response structure.
 * This is the complete response from the GET /reviews/{review_id} endpoint.
 */
export interface BackendReviewSession {
  review_id: string;
  persona_id: string[];
  status: 'IN-PROGRESS' | 'DONE' | 'FAILED';
  document: string;
  created_at: string;
  created_by: string;
  /** New findings structure for multi-persona reviews */
  findings?: BackendFindingItem[];
  /** Legacy themes structure for backward compatibility */
  themes?: Theme[];
}

/**
 * Frontend interfaces for transformed review data
 * These interfaces are used within the Angular components
 */

/**
 * Persona information extracted from findings.
 * Represents a unique AI reviewer persona.
 */
export interface PersonaInfo {
  /** Generated persona identifier based on persona name */
  persona_id: string;
  /** Display name of the persona - maps from backend Persona_Name */
  name: string;
}

/**
 * Individual review insight/finding.
 * Transformed from backend array structure to individual objects.
 */
export interface ReviewInsight {
  /** Unique identifier for this insight */
  insight_id: string;
  /** Persona that generated this insight */
  persona: PersonaInfo;
  /** Finding description - maps from backend "Finding" field */
  finding: string;
  /** Deviation description - maps from backend "Deviation" field */
  deviation: string;
  /** Section identifier - maps from backend "Original_text_section" field */
  section: string;
  /** Subsection identifier - maps from backend "Original_text_subsection" field */
  subsection: string;
  /** Original text excerpt - maps from backend "Original_text" field */
  original_text: string;
  /** Recommendation text - maps from backend "Recommendation" field */
  recommendation: string;
  /** Whether this finding has been resolved by the user */
  resolved: boolean;
  /** Optional user notes for this finding */
  notes?: string;
  /** Severity level of the finding - maps from backend "Severity_Level" field */
  severity: 'High' | 'Medium' | 'Low';
  /** Optional field for identifying duplicate findings */
  duplicate?: string;
}

/**
 * Extended review session with transformed findings and personas.
 * This is the structure used by the ReviewersDetailsComponent.
 */
export interface ReviewSessionExtended extends ReviewSession {
  /** Array of unique personas extracted from findings */
  personas: PersonaInfo[];
  /** Array of individual findings transformed from backend structure */
  findings: ReviewInsight[];
}

/**
 * Utility class for transforming backend review data to frontend interfaces
 */
export class ReviewDataTransformer {
  /**
   * Normalize severity value to valid type.
   * Handles case-insensitive input and defaults to 'Low' for invalid/missing values.
   *
   * @param value - Raw severity value from backend
   * @returns Normalized severity value ('High', 'Medium', or 'Low')
   */
  private static normalizeSeverity(value: string | undefined): 'High' | 'Medium' | 'Low' {
    if (!value) {
      console.warn('ReviewDataTransformer: Missing severity value, defaulting to Low');
      return 'Low';
    }

    const normalized = value.trim().toLowerCase();

    switch (normalized) {
      case 'high':
        return 'High';
      case 'medium':
        return 'Medium';
      case 'low':
        return 'Low';
      default:
        console.warn(`ReviewDataTransformer: Invalid severity value '${value}', defaulting to Low`);
        return 'Low';
    }
  }

  /**
   * Transform backend findings array structure to frontend ReviewInsight array.
   * Backend returns arrays where each index represents one finding.
   * Frontend needs individual finding objects.
   *
   * @param backendFindings - Array of backend finding items
   * @param personaIds - The persona IDs from the review session
   * @param personaMap - Optional map of persona IDs to Persona objects for name resolution
   * @returns Array of ReviewInsight objects sorted by severity (High → Medium → Low)
   */
  static transformBackendFindings(
    backendFindings: BackendFindingItem[],
    personaIds: string[],
    personaMap?: Map<string, Persona>
  ): ReviewInsight[] {
    if (!backendFindings || backendFindings.length === 0) {
      console.warn('ReviewDataTransformer: No findings in backend response');
      return [];
    }

    const insights: ReviewInsight[] = [];

    try {
      // Iterate through each finding item
      backendFindings.forEach((findingItem, itemIdx) => {
        // Get the length of arrays (all should be same length)
        const findingCount = findingItem.Finding?.length || 0;

        if (findingCount === 0) {
          console.warn(`ReviewDataTransformer: Finding item ${itemIdx} has empty Finding array`);
          return;
        }

        // Create one insight per array index
        for (let i = 0; i < findingCount; i++) {
          // Extract persona name from findings or resolve from persona map
          const personaNameFromFinding = findingItem.Persona_Name?.[i];
          let personaId = '';
          let personaName = 'Unknown Persona';

          if (personaNameFromFinding) {
            // Use persona name from findings
            personaName = personaNameFromFinding;

            // Try to find matching persona ID from the persona map
            if (personaMap) {
              for (const [id, persona] of personaMap.entries()) {
                if (persona.name === personaNameFromFinding) {
                  personaId = id;
                  break;
                }
              }
            }

            // If no matching ID found, generate one from the name
            if (!personaId) {
              personaId = personaNameFromFinding.toLowerCase().replace(/\s+/g, '-');
            }
          } else if (personaIds && personaIds.length > 0) {
            // Fallback to first persona ID if no persona name in findings
            personaId = personaIds[0];
            if (personaMap) {
              const persona = personaMap.get(personaId);
              if (persona) {
                personaName = persona.name;
              } else {
                console.warn(`ReviewDataTransformer: Persona ID ${personaId} not found in persona map`);
              }
            }
          }

          // Extract and normalize severity
          const rawSeverity = findingItem.Severity_Level?.[i];
          const severity = this.normalizeSeverity(rawSeverity);

          // Extract resolved status from backend, default to false if not present
          const resolved = findingItem.Resolved?.[i] ?? false;

          insights.push({
            insight_id: `insight-${itemIdx}-${i}`,
            persona: {
              persona_id: personaId,
              name: personaName
            },
            finding: findingItem.Finding?.[i] || '',
            deviation: findingItem.Deviation?.[i] || '',
            section: findingItem.Original_text_section?.[i] || '',
            subsection: findingItem.Original_text_subsection?.[i] || '',
            original_text: findingItem.Original_text?.[i] || '',
            recommendation: findingItem.Recommendation?.[i] || '',
            resolved: resolved,
            notes: '',
            severity: severity,
            duplicate: findingItem.Duplicate?.[i] || ''
          });
        }
      });

      // Sort by severity: High → Medium → Low
      const severityOrder = { High: 0, Medium: 1, Low: 2 };
      insights.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
    } catch (error) {
      console.error('ReviewDataTransformer: Error transforming findings:', error);
      return [];
    }

    return insights;
  }

  /**
   * Extract unique personas from findings.
   *
   * @param insights - Array of ReviewInsight objects
   * @returns Array of unique PersonaInfo objects
   */
  static extractPersonas(insights: ReviewInsight[]): PersonaInfo[] {
    if (!insights || insights.length === 0) {
      console.warn('ReviewDataTransformer: No insights provided for persona extraction, returning empty array');
      return [];
    }

    const personaMap = new Map<string, PersonaInfo>();

    try {
      insights.forEach(insight => {
        if (insight.persona && !personaMap.has(insight.persona.persona_id)) {
          personaMap.set(insight.persona.persona_id, insight.persona);
        }
      });
    } catch (error) {
      console.error('ReviewDataTransformer: Error extracting personas:', error);
      return [];
    }

    return Array.from(personaMap.values());
  }

  /**
   * Transform complete backend response to frontend ReviewSessionExtended.
   *
   * @param backendSession - Backend review session response
   * @param personaMap - Optional map of persona IDs to Persona objects for name resolution
   * @returns Transformed ReviewSessionExtended object
   */
  static transformReviewSession(
    backendSession: BackendReviewSession,
    personaMap?: Map<string, Persona>
  ): ReviewSessionExtended {
    if (!backendSession) {
      console.error('ReviewDataTransformer: Backend session is null or undefined');
      throw new Error('ReviewDataTransformer: Backend session is null or undefined');
    }

    try {
      // Handle empty or null findings gracefully
      const findings = backendSession.findings || [];
      if (!findings || findings.length === 0) {
        console.warn('ReviewDataTransformer: Review session has no findings, returning empty arrays');
      }

      const insights = this.transformBackendFindings(findings, backendSession.persona_id, personaMap);
      const personas = this.extractPersonas(insights);

      return {
        review_id: backendSession.review_id,
        persona_id: backendSession.persona_id,
        status: backendSession.status,
        document: backendSession.document,
        created_at: backendSession.created_at,
        created_by: backendSession.created_by,
        themes: backendSession.themes || [], // Preserve legacy themes if present
        personas,
        findings: insights
      };
    } catch (error) {
      console.error('ReviewDataTransformer: Error transforming review session:', error);
      throw error;
    }
  }
}
